'use client'

import {UseQueryResult} from '@tanstack/react-query'
import * as React from 'react'
import {useCompanies} from 'src/layouts/main-layout/companies-selector/hooks/use-companies.hook'
import {CompaniesSelectorListType} from 'src/layouts/main-layout/companies-selector/types/companies-selector-list.type'

type CompaniesSelectorContextValue = {
  search: string | undefined
  setSearch: (filter: string | undefined) => void
  activeTab: string
  setActiveTab: (tab: string) => void
  selectedCompanies: CompaniesSelectorListType[]
  handleSetSelectedCompanies: (item: CompaniesSelectorListType, single?: boolean) => void
  queryCompanies: UseQueryResult<CompaniesSelectorListType[], Error>
}

const CompaniesSelectorContext = React.createContext<CompaniesSelectorContextValue | null>(null)

export type Props = {
  children: React.ReactNode
}

const STORAGE_KEY = 'app:companies:selected'

const loadStorageState = () => {
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (raw == null) return []

  const companies = JSON.parse(raw) as CompaniesSelectorListType[]
  return companies
}
export function CompaniesSelectorProvider({children}: Props) {
  const [search, setSearch] = React.useState<string | undefined>()
  const [activeTab, setActiveTab] = React.useState('switch')
  const [selectedCompanies, setSelectedCompanies] = React.useState<CompaniesSelectorListType[]>(loadStorageState())

  const queryCompanies = useCompanies()
  React.useEffect(() => {
    try {
      if (selectedCompanies.length) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedCompanies))
      else window.localStorage.removeItem(STORAGE_KEY)
    } catch {}
  }, [selectedCompanies])

  const handleSetSelectedCompanies = (item: CompaniesSelectorListType, single?: boolean) => {
    const exist = selectedCompanies.some((company) => company.id === item.id)

    if (single) {
      setSelectedCompanies([item])
      return
    }
    if (exist && selectedCompanies.length <= 1) return

    if (exist) setSelectedCompanies(selectedCompanies.filter((company) => company.id !== item.id))
    else setSelectedCompanies((prev) => [...prev, item])
  }

  const value = React.useMemo(
    () => ({
      search,
      setSearch,
      activeTab,
      setActiveTab,
      selectedCompanies,
      handleSetSelectedCompanies,
      queryCompanies,
    }),
    [search, setSearch, activeTab, setActiveTab, selectedCompanies, handleSetSelectedCompanies, queryCompanies],
  )

  return <CompaniesSelectorContext.Provider value={value}>{children}</CompaniesSelectorContext.Provider>
}

export function useCompaniesSelector() {
  const ctx = React.useContext(CompaniesSelectorContext)
  if (!ctx) {
    throw new Error('useCompaniesSelector must be used within <CompaniesSelectorProvider />')
  }
  return ctx
}
